import { inject, Injectable } from '@angular/core';
import {
    AccountApplicationUserResponse,
    AccountUserControllerService,
    CreateAccountUserRequest,
    UpdateAccountApplicationUserRequest,
} from '@core/generated/openapi';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    Validators,
} from '@angular/forms';
import { Observable } from 'rxjs';
import { FlagsService } from '@services/flags/flags.service';
import { FEATURE_FLAGS } from '@core/constants/flags.constants';

import type { Option } from '@rcp/common-web';
@Injectable({
    providedIn: 'root',
})
export class AccountUsersService {
    private readonly flagsService = inject(FlagsService);

    constructor(
        private service: AccountUserControllerService,
        private formBuilder: FormBuilder,
    ) {}

    // @HttpCache('accountUsers', 120000)
    findAllAccountUsers(): Observable<Array<AccountApplicationUserResponse>> {
        return this.service.findAllAccountUsers();
    }

    findAccountUserById(
        id: number,
    ): Observable<AccountApplicationUserResponse> {
        return this.service.getAccountUserById(id);
    }

    getCreateForm(): FormGroup {
        return this.formBuilder.group({
            username: new FormControl('', {
                validators: [Validators.required, Validators.maxLength(50)],
                updateOn: 'change',
            }),
            firstName: new FormControl('', {
                validators: [Validators.required, Validators.maxLength(30)],
                updateOn: 'change',
            }),
            lastName: new FormControl('', {
                validators: [Validators.required, Validators.maxLength(100)],
                updateOn: 'change',
            }),
            email: new FormControl('', {
                validators: [
                    Validators.required,
                    Validators.email,
                    Validators.maxLength(255),
                ],
                updateOn: 'change',
            }),
            primaryRoleId: new FormControl('', {
                validators: [Validators.required],
                updateOn: 'change',
            }),
            accountLocationIds: new FormControl('', {
                validators: [Validators.required],
                updateOn: 'change',
            }),
            primaryAccountLocationIdChooser: new FormControl('', {
                validators: [Validators.required],
                updateOn: 'change',
            }),
            primaryAccountLocationId: new FormControl('', {
                updateOn: 'change',
            }),
            password: new FormControl('', {
                validators: [
                    Validators.required,
                    Validators.minLength(8),
                    Validators.maxLength(50),
                    Validators.pattern(
                        /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{6,100})/,
                    ),
                ],
                updateOn: 'change',
            }),
            allLocations: new FormControl(false, {
                updateOn: 'change',
            }),
        });
    }

    getUpdateForm(accountUser: AccountApplicationUserResponse): FormGroup {
        return this.formBuilder.group({
            applicationUserId: new FormControl(accountUser.id),
            firstName: new FormControl(accountUser.firstName, {
                validators: [Validators.required, Validators.maxLength(30)],
                updateOn: 'change',
            }),
            lastName: new FormControl(accountUser.lastName, {
                validators: [Validators.required, Validators.maxLength(100)],
                updateOn: 'change',
            }),
            email: new FormControl(accountUser.email, {
                validators: [
                    Validators.required,
                    Validators.email,
                    Validators.maxLength(255),
                ],
                updateOn: 'change',
            }),
            //roleIds: new FormControl(accountUser.roles),
            primaryRoleId: new FormControl(accountUser.primaryRole.id, {
                validators: [Validators.required],
                updateOn: 'change',
            }),
            accountLocationIds: new FormControl(
                Array.from(accountUser.accountLocations).map(
                    (location) => location.id,
                ),
                {
                    validators: [Validators.required],
                    updateOn: 'change',
                },
            ),
            primaryAccountLocationIdChooser: new FormControl(
                this.getPrimaryAccountLocationChooserValue(accountUser),
                {
                    validators: [Validators.required],
                    updateOn: 'change',
                },
            ),
            primaryAccountLocationId: new FormControl(
                accountUser.primaryAccountLocation?.id,
                {
                    updateOn: 'change',
                },
            ),
            username: new FormControl(
                accountUser.username,
                Validators.maxLength(50),
            ),
            allLocations: new FormControl(accountUser.allLocations ?? false, {
                updateOn: 'change',
            }),
        });
    }

    private getPrimaryAccountLocationChooserValue(
        accountUser: AccountApplicationUserResponse,
    ) {
        if (
            accountUser.allLocations &&
            this.flagsService.isFlagActive(FEATURE_FLAGS.ALL_LOCATIONS)
        ) {
            return 0;
        } else {
            return accountUser.primaryAccountLocation?.id;
        }
    }

    // @InvalidatesCacheTag('accountUsers')
    updateUser(formGroup: FormGroup, locations: Option[]) {
        const form = formGroup.getRawValue();
        let updateRequest = {
            applicationUserId: form.applicationUserId,
            firstName: form.firstName,
            lastName: form.lastName,
            email: form.email,
            primaryRoleId: form.primaryRoleId,
            accountLocationIds: form.accountLocationIds,
            primaryAccountLocationId: this.getPrimaryAccountLocationIdForUpdate(
                formGroup,
                locations,
            ),
            username: form.username,
            allLocations: form.primaryAccountLocationIdChooser === 0,
        } as UpdateAccountApplicationUserRequest;

        return this.service.updateAccountUser(updateRequest);
    }

    private getPrimaryAccountLocationIdForUpdate(
        formGroup: FormGroup,
        locations: Option[],
    ) {
        const form = formGroup.getRawValue();

        if (form.primaryAccountLocationIdChooser === 0) {
            // If All Locations is selected - return either the previously selected primary account location or the first location in the list.
            return form.primaryAccountLocationId
                ? form.primaryAccountLocationId
                : locations[1].value;
        } else {
            return form.primaryAccountLocationIdChooser;
        }
    }

    // @InvalidatesCacheTag('accountUsers')
    createUser(createForm: FormGroup, locations: Option[]) {
        const form = createForm.getRawValue();

        let createRequest = {
            firstName: form.firstName,
            lastName: form.lastName,
            email: form.email,
            primaryRoleId: form.primaryRoleId,
            accountLocationIds: form.accountLocationIds,
            primaryAccountLocationId: this.getPrimaryAccountLocationIdForUpdate(
                createForm,
                locations,
            ),
            username: form.username,
            password: form.password,
            allLocations: form.primaryAccountLocationIdChooser === 0,
        } as CreateAccountUserRequest;

        return this.service.createAccountUser(createRequest);
    }

    // @InvalidatesCacheTag('accountUsers')
    updateStatus(
        accountUserId: number,
        status: AccountApplicationUserResponse.StatusEnum,
    ) {
        if (status === AccountApplicationUserResponse.StatusEnum.Inactive) {
            return this.service.deactivateAccountUser(accountUserId);
        } else {
            return this.service.activateAccountUser(accountUserId);
        }
    }

    resetPassword(restForm: FormGroup) {
        return this.service.setTemporaryPassword(restForm.getRawValue());
    }
}
